import * as actionTypes from './actionTypes';
import { UserGroup, UserGroupActions } from '../../types';
import { showGrowlNotificationTranslated } from '../actions';
import {
  createOrUpdateUserGroup,
  convertUserGroupToUserGroupRequest,
  deleteUserGroup as deleteUserGroupApi,
  devLogger,
  getAllUserGroups,
} from '../../utils';
import { AppThunk } from './';
import { isAxiosError } from 'axios';

export const userGroupRequestStart = (): UserGroupActions => {
  return {
    type: actionTypes.USER_GROUPS_REQUEST_START,
  };
};

export const getUserGroupsSuccess = (userGroups: UserGroup[]): UserGroupActions => {
  return {
    type: actionTypes.USER_GROUPS_GET_SUCCESS,
    userGroups,
  };
};

export const userGroupRequestError = (errorMessage: string): UserGroupActions => ({
  type: actionTypes.USER_GROUPS_REQUEST_ERROR,
  errorMessage,
});

export const getUserGroups = (): AppThunk<Promise<void>> => async dispatch => {
  dispatch(userGroupRequestStart());
  const userGroups: UserGroup[] | null = await getAllUserGroups();
  if (!userGroups) {
    dispatch(showGrowlNotificationTranslated('apiErrors:userGroup.getAllGroupsError', 'error'));
    dispatch(userGroupRequestError('User group get all api error'));
    return;
  }

  // REMOVE BILLING DETAILS FOR MVP START **************************************
  for (const userGroup of userGroups) {
    delete userGroup.default_billing_address;
    delete userGroup.default_billing_contact;
  }
  // REMOVE BILLING DETAILS FOR MVP END ****************************************

  const userGroupsNew = userGroups.map(group => ({ ...group, modified: false }));
  dispatch(getUserGroupsSuccess(userGroupsNew));
};

export const addEmptyUserGroup = (): UserGroupActions => ({
  type: actionTypes.USER_GROUP_ADD_EMPTY_GROUP,
});

export const removeUserGroup = (id: string): UserGroupActions => {
  return {
    type: actionTypes.USER_GROUP_DELETE_GROUP,
    id,
  };
};

export const userGroupsSelectedClear = (): UserGroupActions => ({
  type: actionTypes.USER_GROUP_SELECTED_CLEAR,
});

export const deleteUserGroup =
  (userGroup: UserGroup): AppThunk<Promise<void>> =>
  async (dispatch, getState) => {
    if (userGroup.newItem) {
      dispatch(removeUserGroup(userGroup.id!));
    } else {
      dispatch(userGroupRequestStart());

      const deleted = await deleteUserGroupApi(userGroup.id!).catch(error => {
        if (isAxiosError(error) && error.response?.data?.status === 409) {
          switch (error.response.data.message) {
            case 'User group contains active users.':
              dispatch(showGrowlNotificationTranslated('apiErrors:userGroup.deleteUserGroupErrorActiveUsers', 'error'));
              break;
            case 'User group is approver in approval chain.':
              dispatch(showGrowlNotificationTranslated('apiErrors:userGroup.deleteUserGroupErrorApproverInApprovalChain', 'error'));
              break;
            case 'User group has pending approval chain steps.':
              dispatch(showGrowlNotificationTranslated('apiErrors:userGroup.deleteUserGroupErrorPendingApprovalChainSteps', 'error'));
              break;
            default:
              dispatch(showGrowlNotificationTranslated('apiErrors:userGroup.deleteUserGroupError', 'error'));
              break;
          }
        } else {
          dispatch(showGrowlNotificationTranslated('apiErrors:userGroup.deleteUserGroupError', 'error'));
        }
        dispatch(userGroupRequestError('User group delete api error'));
        return false;
      });

      if (!deleted) {
        return;
      }

      const selectedUserGroup = getState().userGroups.selectedUserGroup;
      if (userGroup.id === selectedUserGroup?.id) {
        dispatch(userGroupsSelectedClear());
      }
      dispatch(removeUserGroup(userGroup.id!));
    }
  };

export const updateUserGroup = (userGroup: UserGroup, userGroupId?: string): UserGroupActions => {
  const id = userGroupId || userGroup.id;
  return {
    type: actionTypes.USER_GROUP_UPDATE_GROUP,
    userGroup: userGroup,
    userGroupId: id!,
  };
};

export const selectUserGroup = (userGroupId: string): UserGroupActions => ({
  type: actionTypes.USER_GROUP_SELECT_GROUP,
  userGroupId,
});

export const saveUserGroup =
  (userGroup: UserGroup, selectGroupAfterSaving: boolean): AppThunk<Promise<void>> =>
  async dispatch => {
    dispatch(userGroupRequestStart());
    const groupToSave = { ...userGroup };
    delete groupToSave.modified;
    if (userGroup.newItem) {
      // Create new group
      delete groupToSave.newItem;

      const userGroupRequest = convertUserGroupToUserGroupRequest(groupToSave);
      const userGroup = await createOrUpdateUserGroup(userGroupRequest, 'create');
      if (!userGroup) {
        dispatch(showGrowlNotificationTranslated('apiErrors:userGroup.saveUserGroupError', 'error'));
        dispatch(userGroupRequestError('User group create api error'));
        return;
      }

      dispatch(updateUserGroup({ ...userGroup, modified: false }, groupToSave.id));
    } else {
      // Update user group with id
      devLogger('Saving user group to api', groupToSave);
      const userGroupRequest = convertUserGroupToUserGroupRequest(groupToSave);
      const userGroup = await createOrUpdateUserGroup(userGroupRequest, 'update', groupToSave.id);
      if (!userGroup) {
        dispatch(showGrowlNotificationTranslated('apiErrors:userGroup.saveUserGroupError', 'error'));
        dispatch(userGroupRequestError('User group update api error'));
        return;
      }

      devLogger('User group after saving from api', groupToSave);
      dispatch(updateUserGroup(groupToSave));
      if (selectGroupAfterSaving) {
        dispatch(selectUserGroup(groupToSave.id!));
      }
    }
  };
